<script setup lang="ts">
  import { lazyLoadComponent } from '@/helpers/lazyLoadComponent'
  import { onMounted, watch } from 'vue'
  import { gsap } from 'gsap'
  const MediaPlatformLinks = lazyLoadComponent({
    comp: () => import('@/components/Home/MediaPlatformLinks.vue'),
  })
  const route = useRoute()

  const tl = gsap.timeline()

  watch(
    () => route.meta.menuTitle,
    () => {
      gsap.from('.menu-title', { opacity: 0, y: '50%', stagger: 0.2, clearProps: true })
    },
  )
  onMounted(() => {
    tl.from('.menu-title', { opacity: 0, y: '50%', stagger: 0.2, clearProps: true }, '<50%')
  })
</script>
<template>
  <!-- `has-world-toggle` is the single switch for the whole header treatment: declaring
       `meta.worldToggle` on a route both renders the toggle below the title and drops the
       tab bar under the header. Adding a new toggle page needs nothing but that meta. -->
  <div
    v-if="!$route.meta.fullPage"
    class="main-route-view"
    :class="[$route.matched.map((x) => x.name), { 'has-world-toggle': $route.meta.worldToggle }]"
  >
    <ContentWrapper>
      <div class="page-header">
        <div class="header-image" />
        <div>
          <h1 class="menu-title">{{ $route.meta.menuTitle }}</h1>
          <h2 v-show="$route.meta.subTitle" class="xs menu-title text-uppercase">{{ $route.meta.subTitle }}</h2>
        </div>
        <!-- header-level page switchers, opted into per route via meta -->
        <CwWorldToggle v-if="$route.meta.worldToggle" v-bind="$route.meta.worldToggle" />
      </div>
      <RouterView />
    </ContentWrapper>
    <PageBanners />
  </div>
  <RouterView v-else />
  <MediaPlatformLinks />
</template>

<style lang="scss" scoped>
  .main-route-view {
    padding-bottom: 40px;
    min-height: 50vw;
    background-size: 100% auto;
    background-repeat: no-repeat;
    // just for temporary default setting

    background-color: #f2f2f2;
    background-image: url('@/assets/imgs/page-backgrounds/rankings.webp');
    .page-header {
      position: relative;
      width: 100%;
      aspect-ratio: 100/14;
      display: flex;
      flex-direction: column;
      justify-content: center;
      line-height: 1em;
      order: 1;
      .header-image {
        position: absolute;
        width: 100%;
        height: 100%;
        background: no-repeat calc(100% - 20px) calc(100% + 8px) / 55% auto;
        pointer-events: none;
      }
      h1,
      h2 {
        color: white;
        margin: 0;
        // text-shadow: 0 0 4px black;
      }
      h2 {
        font-family: 'Glegoo';
        line-height: 1.2em;
        display: inline-block;
        backdrop-filter: blur(10px);
        border-radius: 10px;
        padding: 0 4px;
      }
    }
    .header-image {
      background-image: url('@/assets/imgs/page-backgrounds/rankings-header.webp');
    }

    &.rankings {
      background-image: url('@/assets/imgs/page-backgrounds/rankings.webp');
      .header-image {
        background-image: url('@/assets/imgs/page-backgrounds/rankings-header.webp');
      }
    }
    &.news,
    &.general-post {
      background-image: url('@/assets/imgs/page-backgrounds/news.webp');
      .header-image {
        background-image: url('@/assets/imgs/page-backgrounds/news-header.webp');
      }
    }
    &.community {
      background-image: url('@/assets/imgs/page-backgrounds/community.webp');
      .header-image {
        background-image: url('@/assets/imgs/page-backgrounds/community-header.webp');
      }
    }
    &.support {
      background-image: url('@/assets/imgs/page-backgrounds/support.webp');
      .header-image {
        background-image: url('@/assets/imgs/page-backgrounds/support-header.webp');
      }
    }
    &.game {
      background-image: url('@/assets/imgs/page-backgrounds/game.webp');
      .header-image {
        background-image: url('@/assets/imgs/page-backgrounds/game-header.webp');
      }
    }
    &.maple-lane {
      background-image: url('@/assets/imgs/page-backgrounds/maple-lane.webp');
      background-color: #c7dc5c;
      .header-image {
        background: no-repeat 100% 100% / 35% auto;
        background-image: url('@/assets/imgs/page-backgrounds/maple-lane-header.webp');
      }
    }
    &.pixel-picks {
      background-image: url('@/assets/imgs/page-backgrounds/pixel-picks.webp');
      background-color: #53bbec;
      .header-image {
        background-image: url('@/assets/imgs/page-backgrounds/pixel-picks-header.webp');
        background-size: auto 100%;
        background-position: top right;
      }
      @media #{$tablet} {
        background-image: url('@/assets/imgs/page-backgrounds/pixel-picks-tablet.webp');
        .header-image {
          background: none;
        }
      }
      @media #{$mobile} {
        background-image: url('@/assets/imgs/page-backgrounds/pixel-picks-mobile.webp');
        .header-image {
          background: none;
        }
      }
    }
    &.my-maple {
      background-image: url('@/assets/imgs/page-backgrounds/my-maple.webp');
      background-color: #464138;
      .header-image {
        background: no-repeat 104% 60% / 50% auto;
        background-image: url('@/assets/imgs/page-backgrounds/my-maple-header.webp');
      }
    }
    &.em-classic,
    &.classes-and-jobs-classic,
    &.maple-guides-classic {
      background-image: url('@/assets/imgs/page-backgrounds/classic-world.webp');
      background-color: #3c3330;
      .header-image {
        background: no-repeat calc(100% - 20px) calc(100% + 8px) / 33.73% auto;
        background-image: url('@/assets/imgs/page-backgrounds/classic-world-header.webp');
      }
      .page-header {
        h1,
        h2 {
          color: black;
        }
      }
    }
  }
  #main-menu-body {
    font-size: 18px;
  }

  // A page carrying the world toggle fits a title, a subtitle and a 50px control into the
  // header, so on desktop it gets a floor as well as a ratio: the ratio alone shrinks with the
  // column and would run the control out of room. Keyed on the toggle rather than on the
  // Classic wrapper, so a new toggle page needs nothing but `meta.worldToggle`.
  @media #{$pcOnly} {
    .main-route-view.has-world-toggle .page-header {
      min-height: 200px;
    }
  }
  // Between the tablet artboard and the 1600 content column the column stops growing but the
  // header keeps the site-wide 100/14 ratio, which leaves the toggle short of room under the
  // title and subtitle. A taller ratio gives it that room. Bounded below by 1025 so the 1024
  // artboard's own header box (auto + min-height, further down) is untouched.
  @media #{$screen} and (min-width: #{$tabletSize + 1}) and (max-width: #{$contentMaxWidthSize}) {
    .main-route-view.has-world-toggle .page-header {
      aspect-ratio: 100/16;
    }
  }

  @media #{$tablet} {
    .page-header {
      h1 {
        font-size: 30px;
      }
      h2 {
        font-size: 18px;
      }
    }
    // The box is keyed on the toggle, not on Classic: 48 title + 24 subtitle + the 50px
    // control needs more than the 138 the site-wide 100/14 ratio yields at a 984 column, and
    // that is true of any page carrying the toggle. A new one only has to declare
    // `meta.worldToggle`.
    .main-route-view.has-world-toggle .page-header {
      aspect-ratio: auto;
      min-height: 174px;
    }
    // Classic World has its own 1024 artboard
    .main-route-view.em-classic,
    .main-route-view.classes-and-jobs-classic,
    .main-route-view.maple-guides-classic {
      // The 1024 artboard reserves 423.1 of its 979-wide header for the cluster, right edge
      // 44 in from the header's: 423.1/979 = 43.22% wide, and 4.49% / (100% - 43.22%) =
      // 7.91% for the free-space percentage again.
      //
      // Sprite-for-sprite the artboard draws them ~7% larger than this (its slime is tucked
      // right against the mushroom, where the PC board leaves a 39 gap, so the same three
      // sprites need less width there). One baked bitmap cannot have both gaps, and matching
      // the reserved box is the safer half of that trade: sizing to the sprites instead would
      // push the slime 30px further left, into the title.
      .header-image {
        background-size: 43.22% auto;
        background-position: right 4.2% bottom -11px;
      }
    }
  }

  @media #{$mobile} {
    .main-route-view {
      &.rankings {
        background-image: url('@/assets/imgs/page-backgrounds/rankings-mobile.webp');
      }
      &.news-main,
      &.general-post {
        background-image: url('@/assets/imgs/page-backgrounds/news-mobile.webp');
      }
      &.community {
        background-image: url('@/assets/imgs/page-backgrounds/community-mobile.webp');
      }
      &.support {
        background-image: url('@/assets/imgs/page-backgrounds/support-mobile.webp');
      }
      &.game {
        background-image: url('@/assets/imgs/page-backgrounds/game-mobile.webp');
        &.sixth-job-skills {
          background-image: url('@/assets/imgs/page-backgrounds/game-6th-job-skills.webp');
        }
      }
      &.maple-lane {
        background-image: url('@/assets/imgs/page-backgrounds/maple-lane-mobile.webp');
      }
      &.em-classic,
      &.classes-and-jobs-classic,
      &.maple-guides-classic {
        background-image: url('@/assets/imgs/page-backgrounds/classic-world-mobile.webp');
      }
    }
    .page-header {
      text-align: center;
      padding: 20px 0 20px;
      aspect-ratio: initial;
      // h1,
      // h2 {
      //   -webkit-text-stroke: 0.2em rgba(0, 0, 0, 0.5);
      //   paint-order: stroke;
      // }
    }
    .header-image {
      display: none;
    }
    :deep(.content) {
      display: flex;
      flex-direction: column;
      .page-header {
        order: 0;
      }
    }
    // A page that carries the world toggle puts the tab bar *under* the header — after the
    // title, the subtitle and the toggle (Classic mobile artboard: toggle at 229, main nav
    // at 299). At every other width that already happens, because `.content` is a plain
    // block there and DOM order wins; it is only here, where the column becomes a flex
    // container, that FilterBox's own `order: -1` pulls the bar above the header.
    //
    // So this just neutralises that pull — DOM order is already what the design wants, and
    // dropping to `order: 0` restores it without touching the shared component. Keyed on
    // `has-world-toggle` rather than a route name so that declaring `meta.worldToggle` is
    // the only thing a new toggle page has to do. The bar stays `position: sticky`, so it
    // still pins to the top of the viewport on scroll exactly as it does everywhere else.
    .main-route-view.has-world-toggle :deep(.content) {
      .filterbox-shadow,
      .filterbox {
        order: 0;
      }
      // FilterBox's mobile rule is `margin: 0 -10px` for the full-bleed bar, which also
      // clears the bottom margin it carries at other widths — so without this the bar sits
      // flush against the first card. The artboard leaves 20.
      .filterbox {
        margin-bottom: 20px;
      }
    }
  }
</style>
